import { queryClient } from "@/lib/queryClient";
import portfolioService from "@/services/portfolioService";
import {
  portfolioStrategyQueryParam,
  type StrategyName,
} from "@/config/strategyBrokers";

const PORTFOLIO_STALE_MS = 60 * 1000;
const FYERS_USER_STALE_MS = 5 * 60 * 1000;

export const fyersUserByUidKey = (uid?: string) =>
  ["fyers", "user-by-uid", uid] as const;

/**
 * Deduped/cached read of `/fyers/user/{uid}`. Shares one cache entry so the
 * dashboard reuses the value seeded from the sync-user bootstrap response
 * instead of firing another round-trip on first login.
 */
export function ensureFyersUserByUid(firebaseUid: string) {
  return queryClient.fetchQuery({
    queryKey: fyersUserByUidKey(firebaseUid),
    queryFn: () => portfolioService.getFyersUserByFirebaseUid(firebaseUid),
    staleTime: FYERS_USER_STALE_MS,
  });
}

/**
 * Cache bucket for a strategy. Strategies that resolve to the same backend
 * data share a bucket (Optimus and Polaris Lite both use the default `portfolio`
 * table → "optimus"), so switching between them never triggers a refetch.
 */
export const portfolioCacheStrategy = (strategy?: StrategyName | null): string =>
  portfolioStrategyQueryParam(strategy) ?? "optimus";

export const portfolioCachedKey = (
  clientId?: string,
  strategy?: StrategyName | null
) => ["portfolio", "cached", clientId, portfolioCacheStrategy(strategy)] as const;

export const portfolioDashboardKey = (
  clientId?: string,
  strategy?: StrategyName | null
) =>
  ["portfolio", "dashboard", clientId, portfolioCacheStrategy(strategy)] as const;

/** Deduped read of Postgres-cached portfolio (shared in-flight per client+strategy). */
export function ensureCachedPortfolio(
  clientId: string,
  strategy?: StrategyName | null,
  options?: { force?: boolean }
) {
  if (options?.force) {
    queryClient.removeQueries({
      queryKey: portfolioCachedKey(clientId, strategy),
    });
  }
  return queryClient.fetchQuery({
    queryKey: portfolioCachedKey(clientId, strategy),
    queryFn: () => portfolioService.getCachedPortfolio(clientId, strategy),
    staleTime: options?.force ? 0 : PORTFOLIO_STALE_MS,
  });
}

/** Deduped live portfolio calculation (only needed when cache is empty/stale). */
export function ensurePortfolioDashboard(
  clientId: string,
  strategy?: StrategyName | null,
  options?: { force?: boolean }
) {
  if (options?.force) {
    queryClient.removeQueries({
      queryKey: portfolioDashboardKey(clientId, strategy),
    });
  }
  return queryClient.fetchQuery({
    queryKey: portfolioDashboardKey(clientId, strategy),
    queryFn: () => portfolioService.getPortfolioDashboard(clientId, strategy),
    staleTime: options?.force ? 0 : PORTFOLIO_STALE_MS,
  });
}

export function invalidatePortfolioQueries(
  clientId?: string,
  strategy?: StrategyName | null
) {
  const cachedKey = clientId
    ? strategy != null
      ? portfolioCachedKey(clientId, strategy)
      : (["portfolio", "cached", clientId] as const)
    : (["portfolio", "cached"] as const);
  const dashboardKey = clientId
    ? strategy != null
      ? portfolioDashboardKey(clientId, strategy)
      : (["portfolio", "dashboard", clientId] as const)
    : (["portfolio", "dashboard"] as const);

  queryClient.invalidateQueries({ queryKey: cachedKey });
  queryClient.invalidateQueries({ queryKey: dashboardKey });
}
